import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';
import { controlInvalidClass, controlSurfaceClass } from './controlStyles';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectProps = {
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  invalid?: boolean;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'aria-invalid'>;

/** Native select with chevron — ref-forwarding for react-hook-form `register`. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, disabled, invalid = false, ...props },
  ref,
) {
  return (
    <div className={cn('relative', className)}>
      <select
        ref={ref}
        disabled={disabled}
        className={cn(
          controlSurfaceClass,
          'h-8 appearance-none py-0 pl-2.5 pr-8',
          invalid && controlInvalidClass,
          disabled && 'opacity-60',
        )}
        {...props}
        aria-invalid={invalid || undefined}
      >
        {placeholder != null ? (
          // Keep this selectable. `disabled` made "None" / "Select…" a one-way
          // door: after a choice, an optional field could not be cleared.
          // A `required` select still fails validation while this empty first
          // option is the one selected.
          <option value="">{placeholder}</option>
        ) : null}
        {options.map(opt => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-ds-muted"
        aria-hidden
      />
    </div>
  );
});
